import { compileChecks } from "@mx/gpl-common";
import { cs } from "../../core/classSet";
import { isValidImageAttachment } from "../../core/utils";
import { IProcedureRowType } from "../../graphql/gpl-graphQLTypes.gen";
import { IAttachment, IProcedureRow } from "../../typings/filterEntities";
import { NoArgCallback } from "../../typings/utils";
import Chevron from "../Chevron/Chevron";
import { ConditionalIcon } from "../CommonIcons/CommonIcons";
import {
  ProcedureAttachment,
  ProcedureCheckboxInput,
  ProcedureDateInput,
  ProcedureImageUploadInput,
  ProcedureInspectionCheckInput,
  ProcedureMeterInput,
  ProcedureNumberAmountInput,
  ProcedureNumberInput,
  ProcedureRadioInput,
  ProcedureSignatureInput,
  ProcedureTextInput,
  ProcedureYesNoNaInput,
} from "../ProcedureElements/ProcedureElements";
import styles from "./ProcedureRow.module.css";

const MAX_IMAGE_SIZE = 250;
const HORIZONTAL_PADDING = 16;

interface IProps {
  inBanner?: boolean;
  row: IProcedureRow;
  offspringsVisible?: boolean;
  offspringsFieldsCount?: number;
  onToggleOffsprings?: NoArgCallback;
}

export default function ProcedureRow({
  inBanner,
  row,
  offspringsVisible,
  offspringsFieldsCount = 0,
  onToggleOffsprings,
}: IProps) {
  const { label, type, required, description, meter, choices, attachments } = row;

  function renderInput() {
    switch (type) {
      case IProcedureRowType.Text:
        return <ProcedureTextInput />;
      case IProcedureRowType.Number:
        return <ProcedureNumberInput />;
      case IProcedureRowType.NumberAmount:
        return <ProcedureNumberAmountInput />;
      case IProcedureRowType.Date:
        return <ProcedureDateInput />;
      case IProcedureRowType.DateTime:
        return <ProcedureDateInput isDateAndTime={true} />;
      case IProcedureRowType.Meter:
        return (
          <ProcedureMeterInput
            meterName={meter?.name ?? "Meter name"}
            unit={meter?.unit ?? "Unit"}
          />
        );
      case IProcedureRowType.RadioYesnona:
        return <ProcedureYesNoNaInput />;
      case IProcedureRowType.RadioStandardTest:
        return <ProcedureInspectionCheckInput />;
      case IProcedureRowType.File:
        return <ProcedureImageUploadInput />;
      case IProcedureRowType.FileSignature:
        return <ProcedureSignatureInput />;
      case IProcedureRowType.Radio:
        return (
          <ul>
            {choices?.map((c) => (
              <li className={styles.item} key={c.label}>
                <ProcedureRadioInput label={c.label} required={required} />
              </li>
            ))}
          </ul>
        );
      case IProcedureRowType.Checklist:
        return (
          <ul>
            {choices?.map((c) => (
              <li className={styles.item} key={c.label}>
                <ProcedureCheckboxInput label={c.label} required={required} />
              </li>
            ))}
          </ul>
        );
      case IProcedureRowType.Check:
        return <ProcedureCheckboxInput label={label} required={required} />;
      default:
        compileChecks
          .switchDefault(type)
          .exhaustive<
            IProcedureRowType.Heading | IProcedureRowType.Section | IProcedureRowType.Condition
          >();
        return null;
    }
  }

  if (type === IProcedureRowType.Heading) {
    return <h4 className={cs(styles.headerRow, inBanner && styles.inverted)}>{label}</h4>;
  }

  const handleOnClick = onToggleOffsprings ? () => onToggleOffsprings() : undefined;
  if (type === IProcedureRowType.Section) {
    return (
      <>
        <h2 className={cs(styles.titleWrapper, styles.rowHeading)} onClick={handleOnClick}>
          {handleOnClick ? (
            <Chevron direction={offspringsVisible ? "down" : "right"} size="primary" />
          ) : null}
          <span className={styles.title}>{label}</span>
        </h2>
        {!description && offspringsVisible ? null : (
          <div className={cs(styles.line)}>
            <h4 className={cs(styles.description)}>
              {offspringsVisible
                ? description
                : `${offspringsFieldsCount} field${offspringsFieldsCount > 1 ? "s" : ""} collapsed`}
            </h4>
          </div>
        )}
      </>
    );
  }

  if (type === IProcedureRowType.Condition) {
    const condition: {
      [conditionType: string]: {
        value: {
          op?: string;
          v?: string | number;
          list?: string[];
        };
      };
    } = JSON.parse(row?.condition ?? "{}");
    const action = Object.values(condition)[0]?.value?.op ?? "";
    const value = Object.values(condition)[0]?.value?.v ?? "";
    const list = Object.values(condition)[0]?.value?.list;
    const listValues = list ? `[${list}]` : "";
    const conditionExpanded = `if the answer is "${[action, value, listValues].join(" ")}"`;
    const conditionCollapsed = `${offspringsFieldsCount} conditional field${
      offspringsFieldsCount > 1 ? "s" : ""
    } collapsed`;
    return (
      <div
        className={cs(
          styles.titleWrapper,
          styles.conditionRow,
          !offspringsVisible && styles.conditionCollapsed,
        )}
        onClick={handleOnClick}
      >
        <ConditionalIcon className={styles.conditionIcon} />
        <span className={styles.conditionTitle}>
          {!offspringsVisible ? conditionCollapsed : conditionExpanded}
        </span>
        <Chevron direction={offspringsVisible ? "rotateUp" : "rotateDown"} size="regular" />
      </div>
    );
  }

  const imageAttachments: IAttachment[] = [];
  const fileAttachments: IAttachment[] = [];
  attachments?.forEach((attachment: IAttachment) => {
    if (isValidImageAttachment(attachment)) {
      imageAttachments.push(attachment);
    } else {
      fileAttachments.push(attachment);
    }
  });

  const getAttachmentComponents = (attachments: IAttachment[]) =>
    attachments?.length ? (
      <div className={cs(styles.attachmentWrapper, styles.line)}>
        <ul className={styles.attachmentList}>
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className={cs(styles.attachmentItem, styles.line)}
              style={{ maxWidth: MAX_IMAGE_SIZE + HORIZONTAL_PADDING }}
            >
              <ProcedureAttachment attachment={attachment} />
            </li>
          ))}
        </ul>
      </div>
    ) : undefined;

  return (
    <div className={styles.row}>
      {type !== IProcedureRowType.Check ? (
        <div className={styles.line}>
          <h4 className={cs(styles.label, [styles.required, required])}>{label}</h4>
        </div>
      ) : null}
      {type === IProcedureRowType.Check ? <div className={styles.line}>{renderInput()}</div> : null}
      {description ? (
        <div className={styles.line}>
          <h4 className={cs(styles.description)}>{description}</h4>
        </div>
      ) : null}
      {getAttachmentComponents(imageAttachments)}
      {getAttachmentComponents(fileAttachments)}
      {type !== IProcedureRowType.Check ? <div className={styles.line}>{renderInput()}</div> : null}
    </div>
  );
}
